<template>
  <x-form
    modal
    variant="medium"
    open
    encoding="json"
    method="put"
    :title="title"
    :title-icon-variant="variant === 'authcode' ? undefined : renew ? 'success' : 'danger'"
    :submit-text="$t('Confermo')"
    @submit="onSubmit"
    @cancel="$emit('update:renew', !renew)"
    @update:open="onUpdateOpen"
  >
    <template #description>
      <p v-if="variant === 'autorenew'">
        {{
          renew
            ? $t('Confermi di voler attivare il rinnovo automatico di questo prodotto?')
            : $t('Confermi di voler disattivare il rinnovo automatico di questo prodotto?')
        }}
      </p>
      <p v-else-if="variant === 'authcode'">
        {{ $t("Confermi di voler richiedere l'authcode per procedere con il trasferimento del dominio presso un altro provider?") }}
      </p>
      <p v-else>
        {{
          renew
            ? $t('Confermi di voler rinnovare automaticamente il prodotto alla sua data di scadenza?')
            : $t('Confermi di non voler rinnovare il prodotto alla sua data di scadenza?')
        }}
      </p>
    </template>

    <form-group :name="$t('Inserisci il tuo nome e cognome')">
      <x-input v-model="name" name="name" required />
    </form-group>

    <template v-if="variant === 'authcode'">
      <pf-empty-state v-if="productsState.state !== 'success'">
        <pf-empty-state-header title="Loading" heading-level="h4">
          <template #icon>
            <pf-empty-state-icon>
              <pf-spinner />
            </pf-empty-state-icon>
          </template>
        </pf-empty-state-header>
      </pf-empty-state>

      <form-group required v-else-if="askAuthcodeCancellation" :name="$t('Vuoi anche disdire la registrazione del dominio alla sua scadenza?')">
        <pf-radio
          :label="$t('Sì, voglio disdire')"
          :value="true"
          :checked="cancelProduct === true"
          @change="cancelProduct = true"
          name="cancelProduct"
          required
          ouia-id="authcode-request-cancel-domain"
        />
        <pf-radio
          :label="$t('No, voglio mantenere il dominio')"
          :value="false"
          :checked="cancelProduct === false"
          @change="cancelProduct = false"
          name="cancelProduct"
          required
          ouia-id="authcode-request-keep-domain"
        />
      </form-group>
    </template>

    <form-group v-if="askReason" :name="$t('Inserisci la motivazione')">
      <pf-form-select :model-value="reason" @update:model-value="reason = $as<keyof typeof cancellationReasons>($event)" name="reason" required>
        <pf-form-select-option v-for="(label, key) in cancellationReasons" :key="key" :value="key">
          {{ label }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>
  </x-form>
</template>

<script lang="ts" setup>
import { ref, computed, type Ref } from 'vue';
import { $t as _t } from '@/i18n';
import { asyncComputed } from '@common/asyncComputed';
import { useLoginStore } from '@/store/login';
import { useAlertStore } from '@/store/alerts';
import { ProductOrdersResource, type PublicCancellationReason, type RenewalInfo } from '@/resources';
import type { ComponentProps } from 'vue-component-type-helpers';
import type XForm from './x-form.vue';

type XFormProps = ComponentProps<typeof XForm>;

interface Props extends /* @vue-ignore */ Omit<XFormProps, 'title' | 'variant' | 'titleIconVariant' | 'onSubmit'> {
  idProduct: number;
  variant: 'state' | 'autorenew' | 'authcode';
  renew: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'update:renew', value: boolean): void;
  (e: 'submit'): void;
  (e: 'close'): void;
}>();

const login = useLoginStore();
const alertStore = useAlertStore();

const cancellationReasons: Record<PublicCancellationReason, string> = {
  'Cambio agenzia': login.is_reseller
    ? _t('Fine collaborazione: annullamento per fine del rapporto con il cliente.')
    : _t("Fine collaborazione: annullamento per fine del rapporto con l'agenzia."),
  'Chiusura attività': _t("Chiusura attività: l'azienda ha cessato le sue attività."),
  'Gestione interna': _t('Gestione interna: il servizio è stato internalizzato.'),
  Inutilizzo: _t('Inutilizzo: il servizio non è più utilizzato.'),
  Costi: _t('Costo elevato: Il prezzo è considerato troppo alto.'),
  'Problemi tecnici': _t('Problemi tecnici: il servizio presenta difficoltà che ne compromettono l’uso.'),
  'Assistenza tecnica': _t('Assistenza carente: supporto tecnico insufficiente o inadeguato.'),
};

const name = ref('');
const reason: Ref<PublicCancellationReason | undefined> = ref(undefined);
const cancelProduct: Ref<boolean | undefined> = ref(undefined);

const title = computed(() => {
  if (props.variant === 'autorenew') {
    return props.renew ? _t('Attivazione del rinnovo automatico') : _t('Disattivazione del rinnovo automatico');
  }
  if (props.variant === 'authcode') {
    return _t('Richiesta di authcode');
  }

  return props.renew ? _t('Impostazione del prodotto in rinnovo automatico') : _t('Impostazione del prodotto in "non rinnovare alla scadenza"');
});

const { state: productsState, result: product } = asyncComputed(async () => {
  if (props.variant !== 'authcode') {
    return null;
  }
  return (await new ProductOrdersResource().get(props.idProduct))[0];
});

const askReason = computed(() => (props.variant === 'state' && !props.renew) || (props.variant === 'authcode' && cancelProduct.value));
const askAuthcodeCancellation = computed(() => props.variant === 'authcode' && product.value?.stato_amministrativo !== 'non rinnovare');

async function onSubmit() {
  if (!name.value || (askReason.value && !reason.value)) {
    return;
  }

  const renewalInfo: RenewalInfo = { nominativo: name.value };

  switch (props.variant) {
    case 'autorenew':
      renewalInfo.rinnovo_automatico = props.renew;
      renewalInfo.motivazione_disdetta = props.renew ? undefined : reason.value;
      break;
    case 'state':
      renewalInfo.stato_amministrativo = props.renew ? 'attivo' : 'non rinnovare';
      renewalInfo.motivazione_disdetta = props.renew ? undefined : reason.value;
      break;
    case 'authcode':
      renewalInfo.authcode_request = true;
      renewalInfo.stato_amministrativo = askAuthcodeCancellation.value && cancelProduct.value ? 'non rinnovare' : undefined;
      renewalInfo.motivazione_disdetta = askAuthcodeCancellation.value ? reason.value : undefined;
      break;
  }

  try {
    await new ProductOrdersResource().put(props.idProduct, renewalInfo);
    if (props.variant === 'authcode') {
      alertStore.add({ title: _t('Richiesta inviata, i nostri tecnici ti invieranno il codice authcode al più presto'), type: 'success', toast: true });
    } else {
      alertStore.add({ title: _t('Operazione completata'), type: 'success', toast: true });
    }
  } catch (e) {
    alertStore.add({
      title: _t("Non è stato possibile completare l'operazione. Riprova più tardi o apri un ticket all'assistenza clienti"),
      type: 'danger',
      toast: true,
    });
    emit('update:renew', !props.renew);
  }

  emit('submit');
  emit('update:open', false);
}

function onUpdateOpen(value: boolean) {
  emit('update:open', value);
  if (!value) {
    emit('close');
  }
}
</script>
